import React from "react";

import { MessageDto, TicketDetailDto } from "@megaron/support-contracts";

import { AttachmentsMap, ProcessedAttachment } from "./useSupportTicketPdf";
import { stripSupportTicketMessageMetadata } from "./stripSupportTicketMessageMetadata";

type Props = {
  ticket: TicketDetailDto;
  attachmentsMap: AttachmentsMap;
};

const looksLikeHtml = (body: string): boolean => {
  const trimmed = body.trim();
  return trimmed.startsWith("<") || /<\s*html[\s>]/i.test(trimmed) || /<\s*div[\s>]/i.test(trimmed);
};

const stripHtml = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
};

const formatDate = (date: Date): string =>
  `${date.toLocaleDateString("pl-PL")} ${date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;

type AttachmentBlockProps = { att: ProcessedAttachment; index: number };

const AttachmentBlock: React.FC<AttachmentBlockProps> = ({ att, index }) => {
  if (att.fileType.startsWith("image/")) {
    return (
      <div key={index} style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>{att.fileName}</div>
        <img
          src={`data:${att.fileType};base64,${att.contentBase64}`}
          alt={att.fileName}
          style={{ maxWidth: "100%", maxHeight: 400, display: "block" }}
        />
      </div>
    );
  }

  if (att.fileType === "application/pdf" && att.pdfPageImages.length > 0) {
    return (
      <div key={index} style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>{att.fileName}</div>
        {att.pdfPageImages.map((src, pageIdx) => (
          <img key={pageIdx} src={src} alt={`${att.fileName} str. ${pageIdx + 1}`} style={{ width: "100%", display: "block", marginBottom: 2 }} />
        ))}
      </div>
    );
  }

  return (
    <div key={index} style={{ fontSize: 12, color: "#555", padding: "4px 8px", background: "#f5f5f5", borderRadius: 4, marginBottom: 4 }}>
      {att.fileName}
    </div>
  );
};

type MessageBlockProps = {
  message: MessageDto;
  fromName: string;
  fromEmail: string;
  attachments: ProcessedAttachment[];
};

const MessageBlock: React.FC<MessageBlockProps> = ({ message, fromName, fromEmail, attachments }) => {
  const normalizedBody = stripSupportTicketMessageMetadata(message.body);
  const bodyText = looksLikeHtml(normalizedBody) ? stripHtml(normalizedBody) : normalizedBody;

  return (
    <div style={{ marginBottom: 24, borderBottom: "1px solid #ddd", paddingBottom: 16 }}>
      <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
        <strong style={{ fontSize: 13 }}>{fromName}</strong>
        <span style={{ color: "#666", fontSize: 12 }}>&lt;{fromEmail}&gt;</span>
        <span style={{ color: "#666", fontSize: 11, marginLeft: "auto" }}>{formatDate(new Date(message.createdAt))}</span>
        {message.fromAgent && (
          <span style={{ fontSize: 11, background: "#e8f4fd", padding: "2px 6px", borderRadius: 4 }}>agent</span>
        )}
      </div>
      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
        {bodyText}
      </div>
      {attachments.map((att, i) => (
        <AttachmentBlock key={i} att={att} index={i} />
      ))}
    </div>
  );
};

const resolveFromName = (message: MessageDto, ticket: TicketDetailDto): string => {
  if (message.fromAgent) return message.authorEmail?.split("@")[0] ?? "Agent";
  return ticket.reporterName.trim() || ticket.reporterEmail.split("@")[0];
};

const resolveFromEmail = (message: MessageDto, ticket: TicketDetailDto): string =>
  message.fromAgent ? (message.authorEmail ?? "") : ticket.reporterEmail;

export const SupportTicketPrintView = React.forwardRef<HTMLDivElement, Props>(({ ticket, attachmentsMap }, ref) => {
  const sortedMessages = [...ticket.messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return (
    <div style={{ display: "none" }}>
      <div ref={ref} style={{ padding: 24, fontFamily: "sans-serif", width: 900 }}>
        <h1 style={{ fontSize: 20, marginBottom: 8, marginTop: 0 }}>{ticket.subject} · {ticket.ticketNumber}</h1>
        <div style={{ color: "#666", fontSize: 12, marginBottom: 4 }}>
          {ticket.reporterName} &lt;{ticket.reporterEmail}&gt;
        </div>
        <div style={{ color: "#666", fontSize: 12, marginBottom: 24 }}>{formatDate(new Date(ticket.createdAt))}</div>
        <hr style={{ marginBottom: 24, border: "none", borderTop: "1px solid #ccc" }} />
        {sortedMessages.map((message) => (
          <MessageBlock
            key={message.id}
            message={message}
            fromName={resolveFromName(message, ticket)}
            fromEmail={resolveFromEmail(message, ticket)}
            attachments={attachmentsMap.get(message.id) ?? []}
          />
        ))}
      </div>
    </div>
  );
});

SupportTicketPrintView.displayName = "SupportTicketPrintView";
